import { DateBasePicker } from '@/components/datePicker/DateBasePicker';
import { Box } from '@mui/material';
import {
  DateView,
  DesktopDatePicker,
  MobileDatePicker,
} from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { mobile } from '../../utils/mobile';
import Event from '@mui/icons-material/Event';
import Button from '../button/Button';
import { styles } from './styles';
import { CustomPopper } from './CustomPopper';

interface ResponsiveCalendarInterface {
  value: Date | undefined;
  setOnChange: (value: Date | null) => void;
  useDefault?: boolean;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  mobilePlaceholder?: boolean;
  controlledTextView?: any;
  controllerLabel?: string;
  disabled?: boolean;
  format?: string;
  views?: DateView[];
  useCustomPopper?: boolean;
}

export const ResponsiveCalendar = ({
  value,
  setOnChange,
  useDefault = true,
  maxDate,
  minDate,
  placeholder,
  mobilePlaceholder,
  controlledTextView,
  controllerLabel,
  disabled,
  format,
  views = ['day', 'month', 'year'],
  useCustomPopper = false,
}: ResponsiveCalendarInterface) => {
  const [open, setOpen] = useState(false);
  const [update, setUpdate] = useState(false);
  const { handleResize } = mobile();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    handleResize(setIsMobile);
  }, []);

  useEffect(() => {
    if (update) {
      setOpen(false);
      setUpdate(false);
    }
  }, [value]);

  const controlledView = (children: React.ReactNode) => {
    return controlledTextView
      ? controlledTextView(children, controllerLabel)
      : children;
  };

  const eventIcon = () => <Event sx={styles.iconImage} />;
  const customPopper = (props: any) => (
    <CustomPopper {...props} setOpenPopper={setOpen} />
  );
  return isMobile ? (
    <Box sx={styles.calendarContainer}>
      <DateBasePicker
        children={controlledView(
          <MobileDatePicker
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disabled={disabled}
            format={format}
            label={mobilePlaceholder ? undefined : placeholder}
            views={views}
            minDate={minDate}
            maxDate={maxDate}
            defaultValue={value}
            onMonthChange={(newValue: Date | null) => {
              if (views.includes('day')) return;
              if (newValue == null) {
                return;
              }
              setUpdate(true);
              setOnChange(newValue);
            }}
            onYearChange={(newValue: Date | null) => {
              if (!views.includes('day')) return;
              if (newValue == null) {
                return;
              }
              setUpdate(true);
              setOnChange(newValue);
            }}
            sx={useDefault ? styles.fullWidth : styles.calendar}
            slotProps={{
              layout: { sx: useDefault ? styles.fullWidth : styles.calendar },
            }}
          />
        )}
      />
      <Button
        color={'inherit'}
        disable={disabled}
        iconButton={<Event sx={styles.iconImage} />}
        onClick={() => setOpen(true)}
        sx={styles.icon}
      />
    </Box>
  ) : (
    <Box
      sx={styles.calendarContainer}
      onClick={() => {
        if (!disabled) setOpen(true);
      }}
    >
      <DateBasePicker
        children={controlledView(
          <DesktopDatePicker
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disabled={disabled}
            format={format}
            label={placeholder}
            views={views}
            slots={{
              openPickerIcon: eventIcon,
              popper: useCustomPopper ? customPopper : undefined,
            }}
            minDate={minDate}
            maxDate={maxDate}
            defaultValue={value}
            onMonthChange={(newValue: Date | null) => {
              if (views.includes('day')) return;
              if (newValue == null) {
                return;
              }
              setUpdate(true);
              setOnChange(newValue);
            }}
            onYearChange={(newValue: Date | null) => {
              if (!views.includes('day')) return;
              if (newValue == null) {
                return;
              }
              setUpdate(true);
              setOnChange(newValue);
            }}
            sx={useDefault ? styles.fullWidth : styles.calendar}
            slotProps={{
              textField: { color: 'focus' },
              popper: { sx: { backgroundColor: 'white' } },
              layout: { sx: useDefault ? undefined : styles.calendar },
            }}
          />
        )}
      />
    </Box>
  );
};
